import * as amplitude from '@amplitude/analytics-browser';
import CONFIG from '../settings';
import { useSelector } from 'react-redux';
import { AppState } from '../redux/redux.model';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

type AnalyticsInfo = {
  cropId?: string;
  cropName?: string;
  organizationId?: string;
  organizationName?: string;
  farmId?: string;
  farmName?: string;
  userId?: string;
  userName?: string;
  jobTitle?: string;
  clonedVariables?: number;
  clonedIndicators?: number;
  clonedPhenomena?: number;
  methodologyId?: string;
  inspectionGroupName?: string;
  inspectionGroupID?: string;
  numberOfClusters?: number;
  numberOfComponents?: number;
  clusterNames?: string[];
};

interface TrackingHookReturn {
  track: (message: string, additionalPayload?: AnalyticsInfo) => void;
  identify: (optionalProperties?: Record<string, string>) => void;
  init: () => void;
}

export const trackingEvent = (action: string, additionalInfo: AnalyticsInfo = {}) => {
  const payload: AnalyticsInfo = {
    ...additionalInfo
  };
  amplitude.track(action, payload);
};

export const trackingIdentify = (userId: string) => {
  amplitude.reset();
  amplitude.setUserId(userId);
};

const useTrackingEvents = (): TrackingHookReturn => {
  const { companyId } = useParams<{ companyId: string }>();
  const companies = useSelector((state: AppState) => state.Company?.entities ?? {});
  const company = companyId && companies[companyId] ? companies[companyId] : undefined;
  const property = useSelector((state: AppState) => state.Property?.propertySelect);

  const basePayload: AnalyticsInfo = useMemo(
    () => ({
      organizationId: company?.id,
      organizationName: company?.name,
      farmId: property?.id,
      farmName: property?.name
    }),
    [company, property]
  );

  const handleTrack = useCallback(
    (action: string, additionalInfo: AnalyticsInfo = {}) => {
      amplitude.track(action, { ...additionalInfo, ...basePayload });
    },
    [basePayload]
  );

  const handleInit = () => {
    amplitude.init(CONFIG.amplitudeKey as string, {
      defaultTracking: {
        attribution: false,
        pageViews: false,
        sessions: false,
        fileDownloads: false,
        formInteractions: false
      }
    });
  };

  const handleIdentify = (optionalProperties?: Record<string, string>) => {
    const identifyInstance = new amplitude.Identify();
    for (const [key, value] of Object.entries(basePayload)) {
      if (value !== undefined) {
        identifyInstance.set(key, value);
      }
    }
    if (optionalProperties) {
      for (const [key, value] of Object.entries(optionalProperties)) {
        identifyInstance.set(key, value);
      }
    }
    amplitude.identify(identifyInstance);
  };

  return {
    track: handleTrack,
    identify: handleIdentify,
    init: handleInit
  };
};

export default useTrackingEvents;
